import {
  Annotation,
  Book,
  EnhancedAnnotation,
  KnownCloud,
  KnownUser,
  OverlayAnnotationTypes,
  Tag,
  UserPrefState,
} from '@readcloud/data';
import React from 'react';
import { AnnotationRNDComponent } from './AnnotationRNDComponent';
import { AnnotationsContext } from './AnnotationsContext';

interface Props {
  className?: string;
  outerContainer?: HTMLDivElement;
  pagesContainer?: HTMLDivElement;
  pageContainer?: HTMLDivElement;
  state: { annotations: Annotation[] };
  onAnnotationComment: (id: string) => void;
  onAnnotationChange: (wiget: Annotation) => void;
  onAnnotationDelete: (id: string) => void;
  containerHeight: number | string;
  containerWidth: number | string;
  scale: number;
  editable: boolean;
  /**
   * The zIndex used when an annotation should be displayed BELOW page content.
   * For instance, below a PDFPage text layer, so that the text layer takes selection/interaction precedence
   */
  belowContentZIndex: number;
  /**
   * The zIndex used when an annotation should be displayed ABOVE page content
   */
  aboveContentZIndex: number;
  knownUsers: KnownUser[];
  knownClouds: KnownCloud[];
  selectedAnnotation?: string;
  onSelectedAnnotationChange: (id?: string) => void;
  canEditAnnotation: (annotation: Annotation) => boolean;
  canEditPublisherOptions: (id: string) => boolean;
  mediaContent: { mediaContent: Book[]; tagsContent: Tag[] };
  onSavePreferences?: (type: string, data: Partial<Annotation['data']>) => void;
  annoPrefs?: UserPrefState['annoPrefs'];
  showPublisherOptions?: boolean;
}

/**
 * Use this component to render your annotations and be able to edit them
 */
export const AnnotationStage: React.FC<Props> = (props) => {
  const stageContainerRef = React.useRef<HTMLDivElement>();
  const handleAnnotationClick = (
    annotation: Annotation,
    reference: HTMLElement
  ) => {
    if (
      !props.editable ||
      props.selectedAnnotation === annotation.id ||
      !props.canEditAnnotation(annotation)
    ) {
      return;
    }

    props.onSelectedAnnotationChange(annotation ? annotation.id : undefined);
  };

  const deselectAll = () => {
    props.onSelectedAnnotationChange(undefined);
  };

  return (
    <AnnotationsContext.Provider value={props.mediaContent}>
      <>
        <div
          style={{
            position: 'relative',
            width: props.containerWidth,
            height: props.containerHeight,
          }}
          ref={stageContainerRef}
          className={props.className}
        >
          {props.state.annotations
            .filter((annotation): annotation is EnhancedAnnotation =>
              OverlayAnnotationTypes.includes(annotation.type)
            )
            .map((annotation) => (
              //console.log("Annotations", annotation);
              <AnnotationRNDComponent
                outerContainer={props.outerContainer}
                pagesContainer={props.pagesContainer}
                pageContainer={props.pageContainer}
                containerRef={stageContainerRef}
                showPublisherOptions={props.showPublisherOptions}
                belowContentZIndex={props.belowContentZIndex}
                aboveContentZIndex={props.aboveContentZIndex}
                onComment={props.onAnnotationComment}
                onDelete={props.onAnnotationDelete}
                onSelect={handleAnnotationClick}
                selected={
                  !!props.selectedAnnotation &&
                  props.selectedAnnotation === annotation.id &&
                  props.editable
                }
                onChange={props.onAnnotationChange}
                key={annotation.id}
                annotation={annotation}
                editable={props.editable && props.canEditAnnotation(annotation)}
                scale={props.scale}
                knownUsers={props.knownUsers}
                knownClouds={props.knownClouds}
                onSavePreferences={props.onSavePreferences}
                annoPrefs={props.annoPrefs}
              />
            ))}
        </div>
      </>
    </AnnotationsContext.Provider>
  );
};
